<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import FloatLabel from "primevue/floatlabel";
import { useRoute } from "vue-router";
import { useAuthStore, useAuthModal } from "@/features/auth/store";
import router from "@/router";
import Password from "primevue/password";
import Chip from "primevue/chip";
import Message from "primevue/message";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";

const route = useRoute();
const toast = useToast();
const { t } = useI18n();

const password = ref("");
const passwordError = ref("");

const confirmPassword = ref("");
const confirmPasswordError = ref("");

const token = ref("");
const loadingState = ref(false);

const sixSymbolsValidation = computed(() => password.value.length >= 8);
const numberValidation = computed(() => /\d/.test(password.value));
const bigLetterValidation = computed(() => /\p{Lu}/u.test(password.value));
const smallLetterValidation = computed(() => /\p{Ll}/u.test(password.value));
const specialLetterValidation = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
);

const passwordValidations = computed(() => [
  { label: t("password_validation.min_6_chars"), validation: sixSymbolsValidation.value },
  { label: t("password_validation.one_number"), validation: numberValidation.value },
  {
    label: t("password_validation.one_uppercase"),
    validation: bigLetterValidation.value,
  },
  {
    label: t("password_validation.one_lowercase"),
    validation: smallLetterValidation.value,
  },
  {
    label: t("password_validation.one_special_char"),
    validation: specialLetterValidation.value,
  },
]);

const disableSubmit = computed(() => {
  return (
    password.value === "" ||
    confirmPassword.value === "" ||
    !!passwordError.value.length ||
    !!confirmPasswordError.value.length ||
    !sixSymbolsValidation.value ||
    !numberValidation.value ||
    !bigLetterValidation.value ||
    !smallLetterValidation.value ||
    !specialLetterValidation.value
  );
});

const validatePassword = () => {
  passwordError.value = password.value.length >= 8 ? "" : t("signup.password_error");
};

const validateConfirmPassword = () => {
  if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = t("signup.password_mismatch");
  } else {
    confirmPasswordError.value = "";
  }
};

async function setNewPassword() {
  loadingState.value = true;
  try {
    await useAuthStore().resetPassword({
      token: token.value,
      password: password.value,
    });

    showToast("Твой пароль успешно изменён");
    useAuthModal().hideAuthModal();
  } catch (error) {
    console.error("Password reset failed:", error);
    toast.add({
      severity: "error",
      summary: "Ошибка",
      detail: "Не удалось изменить пароль. Попробуйте еще раз.",
      life: 5000,
    });
  } finally {
    loadingState.value = false;
  }
}

const showToast = (message: string) => {
  toast.add({ severity: "info", summary: "Info", detail: message, life: 3000 });
};

onMounted(() => {
  const { resetToken } = route.params as { resetToken?: string | string[] };
  if (Array.isArray(resetToken)) {
    token.value = resetToken[0] ?? "";
  } else {
    token.value = resetToken ?? "";
  }
  router.replace({ path: "/" });
});
</script>

<template>
  <div class="set-new-password__content">
    <span class="set-new-password__title h2">{{ t("reset_password.title") }}</span>
    <div class="set-new-password__content__wrapper">
      <div class="set-new-password__inputs">
        <div>
          <FloatLabel>
            <Password
              v-model="password"
              @input="validatePassword"
              toggle-mask
              :inputProps="{
                id: 'password',
                name: 'password',
                autocomplete: 'new-password',
              }"
              class="password"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                pcInput: {
                  root: {
                    style: {
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                  input: {
                    id: 'password',
                    name: 'password',
                    autocomplete: 'new-password',
                    'data-form-type': 'password',
                    required: true,
                  },
                },
              }"
            >
              <template #footer>
                <div class="password__validation-chips-wrapper">
                  <Chip
                    v-for="(item, index) in passwordValidations"
                    :key="index"
                    :label="item.label"
                    :icon="`pi ${
                      item.validation ? 'pi-chevron-circle-down' : 'pi-circle'
                    }`"
                    :pt="{
                      root: {
                        style: {
                          backgroundColor: item.validation
                            ? 'rgba(14, 105, 17, 0.05)'
                            : 'rgba(26, 33, 51, 0.05)',
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                      icon: {
                        style: {
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                    }"
                  />
                </div>
              </template>
            </Password>
            <label for="password">{{ t("signup.password_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!passwordError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ passwordError }}
          </Message>
        </div>
        <div>
          <FloatLabel>
            <Password
              v-model="confirmPassword"
              @input="validateConfirmPassword"
              :inputProps="{
                id: 'confirmPassword',
                name: 'confirmPassword',
                type: 'password',
                autocomplete: 'new-password',
              }"
              class="password"
              :feedback="false"
              toggle-mask
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                pcInput: {
                  root: {
                    style: {
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                  input: {
                    id: 'password',
                    name: 'password',
                    type: 'password',
                    autocomplete: 'new-password',
                    'data-form-type': 'password',
                    required: true,
                  },
                },
              }"
            >
            </Password>
            <label for="confirmPassword">{{ t("signup.confirm_password_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!confirmPasswordError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ confirmPasswordError }}
          </Message>
        </div>
      </div>
    </div>
    <div class="set-new-password__submit_btn__wrapper">
      <Button
        class="set-new-password-submit"
        :label="t('reset_password.submit_button')"
        :disabled="disableSubmit"
        :loading="loadingState"
        @click="setNewPassword"
      />
    </div>
  </div>
</template>
<style scoped>
.set-new-password__content {
  display: flex;
  border-radius: 40px;
  padding: 0 40px 40px 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* Figma Authorization flow dialogs = 480 */
  width: min(90vw, 480px);
  max-width: 480px;
}

.new-password-validations {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.set-new-password__title {
  text-align: center;
  margin-bottom: 20px;
}

.set-new-password__content__wrapper {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.set-new-password_third_party button {
  display: flex;
  border-radius: 8px;
  gap: 8px;
  padding: 12px 20px;
}

.set-new-password__inputs {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.set-new-password__inputs__wrapper {
  width: 100%;
}

.password {
  width: 100%;
}

.set-new-password__text {
  display: flex;
  justify-content: flex-end;
}

.set-new-password__submit_btn__wrapper {
  display: flex;
  width: 100%;
  margin-top: 40px;
}

.set-new-password-submit {
  width: 100%;
  border-radius: 100px;
}

.password__validation-chips-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 280px;
}
</style>
